import React, { useEffect, useState } from "react";
import { Link } from 'react-router-dom';
import ButtonCustom from "../ButtonCustom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Chip,
  Autocomplete,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import * as yup from "yup";
import ListHead from "../../components/table/ListHead";
import Scrollbar from "../../components/table/Scrollbar";
import SearchNotFound from "../../components/table/SearchNotFound";
import { StyledTableRow } from "../../components/TableRow";
import FormSearch from "../../components/FormSearch";
import CircularLoader from "../../components/circularLoader";
import { getOnboardingList, getOnboardingDropdown, writeLog, deleteOnboardingApplication } from "../../services/services";
import { applySortFilter } from "../../utils/tableFunctions";
import { pageNumber, rowsPerPage, prefixV3 } from "../../utils/constants";
import Pagination from "../../components/Pagination";
import {
  parseCurrency,
  parseDateTime,
  formatDateFilter,
} from "../../utils/tableFunctions";
import { showConfirmDialog } from "../../utils/toast";
import Label from "../Label";

const TABLE_HEAD = [
  { id: "businessName", label: "Business Entity" },
  { id: "requestor", label: "Requestor" },
  { id: "updated_at", label: "Last Update" },
  { id: "status", label: "Status" },
  { id: "actions", label: "" },
];

const STATUS_OPTIONS = [
  { value: 'PENDING_SUBMISSION', label: 'Pending Submission' },
  { value: 'PENDING_COMPLIANCE_CHECK', label: 'Pending Compliance Check' },
  { value: 'COMPLIANCE_REJECTED', label: 'Compliance Rejected' },
  { value: 'PENDING_FINAL_APPROVAL', label: 'Pending Final Approval' },
  { value: 'FINAL_APPROVED', label: 'Final Approved' },
  { value: 'FINAL_REJECTED', label: 'Final Rejected' }
];

const statusColors = {
  PENDING_SUBMISSION: 'default',
  PENDING_COMPLIANCE_CHECK: 'info',
  COMPLIANCE_REJECTED: 'error',
  PENDING_FINAL_APPROVAL: 'warning',
  FINAL_APPROVED: 'success',
  FINAL_REJECTED: 'error'
};

const statusLabels = {
  PENDING_SUBMISSION: 'Pending Submission',
  PENDING_COMPLIANCE_CHECK: 'Pending Compliance Check',
  COMPLIANCE_REJECTED: 'Compliance Rejected',
  PENDING_FINAL_APPROVAL: 'Pending Final Approval',
  FINAL_APPROVED: 'Final Approved',
  FINAL_REJECTED: 'Final Rejected'
};

const AllOnboarding = ({ userRole }) => {
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("updated_at");
  const [loaded, setLoaded] = useState(false);
  const [tableData, setTableData] = useState({ results: [] });
  const [dropdown, setDropdown] = useState({ business_entity: [], requestor: [] });
  const [pagination, setPagination] = useState({
    page: pageNumber,
    page_size: rowsPerPage,
  });
  const [createDialog, setCreateDialog] = useState(false);

  // Schema for FormSearch component
  // Deduplicate options for Autocomplete (by id) and sort by label
  // Deduplicate and convert options to { value, label } for multi-select
  const businessOptions = (dropdown.business_entity || [])
    .map((b) => ({ value: b.id, label: b.business_name }))
    .filter((v) => v.value)
    .sort((a, b) => a.label.localeCompare(b.label));

  const requesterOptions = (dropdown.requestor || [])
    .map((u) => ({ value: u.id, label: u.email || u.username }))
    .filter((v) => v.value)
    .sort((a, b) => a.label.localeCompare(b.label));

  const schema = [
    {
      type: "multi-select",
      param: "business_entity",
      label: "Business Entity",
      options: businessOptions,
      width: 4,
    },
    {
      type: "multi-select",
      param: "created_by",
      label: "Requestor",
      options: requesterOptions,
      width: 4,
    },
    {
      type: "multi-select",
      param: "status",
      label: "Status",
      options: STATUS_OPTIONS,
      width: 4,
    },
  ];

  const handleFilter = async (filterValues) => {
    setLoaded(false);
    try {
      const params = {
        business_entity: Array.isArray(filterValues.business_entity) && filterValues.business_entity.length > 0
          ? filterValues.business_entity.map(String).join(',')
          : undefined,
        created_by: Array.isArray(filterValues.created_by) && filterValues.created_by.length > 0
          ? filterValues.created_by.map(String).join(',')
          : undefined,
        status: filterValues.status || undefined,
      };
      const { data } = await getOnboardingList(params);
      // Expect backend pagination or array; normalize to array
      const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      const transformedData = results.map(item => {
        // Helper function to format user
        const formatUser = (user) => {
          if (!user) return 'N/A';
          return user.email || user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A';
        };

        return {
          // Must have a unique 'key' for the table component
          key: item.id,

          // Fields for table display
          businessName: item.business_name || 'N/A',
          requestor: formatUser(item.created_by),
          updatedBy: formatUser(item.updated_by),
          status: item.status || 'N/A',
          created_at: item.created_at,
          updated_at: item.updated_at,
        };
      });
      setTableData({ results: transformedData });
    } catch (error) {
      console.error('Error fetching filtered data:', error);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoaded(false);
      try {
        const [listRes, ddRes] = await Promise.all([
          getOnboardingList(),
          getOnboardingDropdown()
        ]);
        const listData = listRes.data;
        const results = Array.isArray(listData?.results) ? listData.results : Array.isArray(listData) ? listData : [];
        const transformedData = results.map(item => {
          // Helper function to format user
          const formatUser = (user) => {
            if (!user) return 'N/A';
            return user.email || user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A';
          };

          return {
            // Must have a unique 'key' for the table component
            key: item.id,

            // Fields for table display
            businessName: item.business_name || 'N/A',
            requestor: formatUser(item.created_by),
            updatedBy: formatUser(item.updated_by),
            status: item.status || 'N/A',
            created_at: item.created_at,
            updated_at: item.updated_at,
          };
        });
        setTableData({ results: transformedData });
        setDropdown(ddRes.data || {});
      } catch (error) {
        console.error('Error fetching onboarding data:', error);
      } finally {
        setLoaded(true);
      }
    };

    fetchData();
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirmDialog("Are you sure you want to delete this onboarding application?");
    if (confirmed) {
      try {
        await deleteOnboardingApplication(id);
        // Refresh the list after successful deletion
        const params = {};
        const { data } = await getOnboardingList(params);
        const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        const transformedData = results.map(item => {
          const formatUser = (user) => {
            if (!user) return 'N/A';
            return user.email || user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A';
          };

          return {
            key: item.id,
            businessName: item.business_name || 'N/A',
            requestor: formatUser(item.created_by),
            updatedBy: formatUser(item.updated_by),
            status: item.status || 'N/A',
            created_at: item.created_at,
            updated_at: item.updated_at,
          };
        });
        setTableData({ results: transformedData });
        writeLog('[Info] - Delete Onboarding Application - Successfully deleted onboarding application')
      } catch (error) {
        console.error('Error deleting onboarding application:', error);
      }
    }
  };

  const filteredUsers = applySortFilter(tableData.results, order, orderBy).sort((a, b) => {
    if (orderBy === "updated_at") return 0;
    const aVal = a.updated_at ?? '';
    const bVal = b.updated_at ?? '';
    if (!aVal && !bVal) return 0;
    if (!aVal) return 1;
    if (!bVal) return -1;
    return bVal.localeCompare(aVal);
  });

  return (

    <Box>
      <FormSearch schema={schema} onSearch={handleFilter} />

      {loaded ? (
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>

              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />
                <TableBody>
                  {filteredUsers.map((row) => {
                    const {
                      key: id,
                      businessName,
                      requestor,
                      updatedBy,
                      status,
                      updated_at
                    } = row;

                    return (
                      <StyledTableRow key={id} hover>
                        <TableCell>
                          {businessName}
                        </TableCell>
                        <TableCell>
                          {requestor}
                        </TableCell>
                        <TableCell>
                          {parseDateTime(updated_at)}
                        </TableCell>
                        <TableCell>
                          {/* <Chip
                            label={statusLabels[status] || status || '-'}
                            color={statusColors[status] || 'default'}
                            size="small"
                          /> */}
                          <Label color={statusColors[status] || 'default'}>
                            {statusLabels[status] || status || '-'}
                          </Label>
                        </TableCell>
                        <TableCell>
                          {status !== 'FINAL_APPROVED' ? (
                            <>
                              {(() => {
                                const hasFinalApproval = userRole && userRole.includes('FinalApproval');
                                const hasComplianceCheck = userRole && userRole.includes('ComplianceCheck');
                                const hasView = userRole && userRole.includes('View');
                                const hasEdit = userRole && userRole.includes('Edit');
                                const canDelete = userRole && userRole.includes('delete');

                                let tooltipTitle = "Edit";
                                let IconComponent = EditIcon;

                                if (!hasEdit && hasFinalApproval) {
                                  IconComponent = status === 'PENDING_FINAL_APPROVAL' ? CheckCircleIcon : VisibilityIcon;
                                  tooltipTitle = status === 'PENDING_FINAL_APPROVAL' ? "Approve" : "View";
                                } else if (!hasEdit && hasComplianceCheck) {
                                  IconComponent = status === 'PENDING_COMPLIANCE_CHECK' ? CheckCircleIcon : VisibilityIcon;
                                  tooltipTitle = status === 'PENDING_COMPLIANCE_CHECK' ? "Compliance Check" : "View";
                                } else if (hasEdit) {
                                  tooltipTitle = "Edit";
                                  IconComponent = EditIcon;
                                } else if (hasView) {
                                  tooltipTitle = "View";
                                  IconComponent = VisibilityIcon;
                                }

                                return (
                                  <>
                                    <Tooltip title={tooltipTitle} placement="top">
                                      <IconButton
                                        size="small"
                                        component={Link}
                                        to={prefixV3 + "/operations/onboarding/edit/" + id}
                                        onClick={() => {
                                          window.localStorage.removeItem('onboarding_draft');
                                          writeLog('[Info] - Click button [Edit Onboarding Application] - Open new tab to Edit Onboarding Application')
                                        }}
                                      >
                                        <IconComponent />
                                      </IconButton>
                                    </Tooltip>
                                    {canDelete && (
                                      <Tooltip title="Delete" placement="top">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDelete(id)}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </>
                                );
                              })()}
                            </>
                          ) : (
                            <Tooltip title="View" placement="top">
                              <IconButton
                                size="small"
                                component={Link}
                                to={prefixV3 + "/operations/onboarding/edit/" + id}
                                onClick={() => {
                                  window.localStorage.removeItem('onboarding_draft');
                                  writeLog('[Info] - Click button [View Onboarding Application] - Open new tab to View Onboarding Application')
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {filteredUsers.length === 0 && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6}>
                        <Box sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={""} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>

            </TableContainer>
          </Scrollbar>

          <Pagination
            pagination={pagination}
            count={tableData.results.length}
            onChange={(page, page_size) => {
              setPagination({ page, page_size });
            }}
          />
        </Card>
      ) : (
        <CircularLoader />
      )}
    </Box>
  );
};

export default AllOnboarding;
